import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Interface for opkaldsdata
interface OpkaldsData {
  id: string;
  user_id?: string;  // Gør user_id valgfri
  timestamp?: {
    seconds: number;
    nanoseconds: number;
  } | number | string;
  call_duration?: number;
  talk_ratio?: number;
  observed_values?: {
    dominance_mean?: number;
    pitch_std?: number;
    energy_std?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Henter brugerens interne ID (user_id) fra users collection
 * @param authUid Firebase authentication user ID
 * @returns Det interne user_id som bruges til at forbinde til rule_result_agent dokumenter
 */
export async function hentBrugerInternId(authUid: string): Promise<string | null> {
  console.log(`hentBrugerInternId: Forsøger at hente for authUid: '${authUid}'`);
  try {
    const userRef = doc(db, 'users', authUid);
    const userDoc = await getDoc(userRef);

    console.log(`hentBrugerInternId: userDoc.exists() for '${authUid}' er: ${userDoc.exists()}`);

    if (!userDoc.exists()) {
      console.log(`hentBrugerInternId: Ingen brugerdokument fundet med auth ID: ${authUid}`);
      return null;
    }
    
    const userData = userDoc.data();
    console.log(`hentBrugerInternId: userData for '${authUid}' er:`, JSON.stringify(userData, null, 2));
    
    if (!userData) {
      console.log(`hentBrugerInternId: Brugerdokumentet (userDoc) eksisterer, men .data() returnerede intet for auth ID: ${authUid}`);
      return null;
    }
    
    if (!userData.user_id) {
      console.log(`hentBrugerInternId: user_id felt ikke fundet (eller er falsy) i brugerdata for auth ID: ${authUid}. userData.user_id var: `, userData.user_id);
      return null;
    }
    
    console.log(`Fandt internt user_id: ${userData.user_id} for auth ID: ${authUid}`);
    return userData.user_id;
  } catch (error) {
    console.error(`hentBrugerInternId: FEJL ved hentning for authUid: '${authUid}'`, error);
    return null;
  }
}

/**
 * Henter brugerdata og observed_values fra Firebase
 * @param authUid Firebase authentication user ID
 * @returns Et objekt med brugerens observed_values eller null hvis ingen data findes
 */
export async function hentBrugerObservedValues(authUid: string) {
  if (!authUid) {
    console.error('Intet auth bruger-ID angivet til hentBrugerObservedValues');
    return null;
  }

  try {
    // 1. Hent brugerens interne ID (P44) fra users collection
    const internUserId = await hentBrugerInternId(authUid);
    
    if (!internUserId) {
      console.error('Kunne ikke finde det interne bruger-ID');
      return null;
    }
    
    console.log(`Søger efter dokumenter med user_id: ${internUserId}`);
    
    // 2. Hent brugerens seneste dokument fra rule_result_agent med det interne ID
    const callsCollection = collection(db, 'rule_result_agent');
    const callsQuery = query(
      callsCollection,
      where('user_id', '==', internUserId), // Bruger internt ID i stedet for auth ID
      orderBy('timestamp', 'desc'),
      limit(1) // Hent kun det seneste dokument
    );

    const callsSnapshot = await getDocs(callsQuery);
    console.log(`Fandt ${callsSnapshot.size} dokumenter med user_id: ${internUserId}`);

    if (callsSnapshot.empty) {
      console.log(`Ingen data fundet for bruger med internt ID: ${internUserId}`);
      return null;
    }

    // 3. Hent data fra det seneste dokument
    const callData = callsSnapshot.docs[0].data();
    
    // 4. Tjek om observed_values findes i dokumentet
    if (!callData.observed_values) {
      console.log('Ingen observed_values fundet i dokumentet');
      return null;
    }

    // 5. Hent de specifikke værdier vi har brug for
    const { dominance_mean, pitch_std, speech_rate_mean } = callData.observed_values;

    // Returner de hentede værdier
    return {
      dominance_mean: dominance_mean !== undefined ? Number(dominance_mean) : null,
      pitch_std: pitch_std !== undefined ? Number(pitch_std) : null,
      speech_rate_mean: speech_rate_mean !== undefined ? Number(speech_rate_mean) : null,
      // Returnerer hele objektet så vi også har adgang til andre værdier hvis nødvendigt
      full_observed_values: callData.observed_values,
      timestamp: callData.timestamp
    };
  } catch (error) {
    console.error('Fejl ved hentning af brugerdata:', error);
    return null;
  }
}

/**
 * Henter brugerdata med flere detaljer for visualisering
 * @param authUid Firebase authentication user ID
 * @returns Detaljeret data om brugerens stemme og præstation
 */
export async function hentKompetenceData(authUid: string) {
  if (!authUid) {
    console.error('Intet auth bruger-ID angivet til hentKompetenceData');
    return null;
  }

  try {
    // Hent de grundlæggende observed_values
    const observedValues = await hentBrugerObservedValues(authUid);
    
    if (!observedValues) {
      return null;
    }

    // Beregn procenter for visualisering
    // Disse maksværdier kan justeres efter behov baseret på data
    const DOMINANCE_MAX = 1.0;
    const PITCH_STD_MAX = 80.0;
    const ENERGY_STD_MAX = 15.0;

    // Beregn procenter (0-100%)
    const dominancePercent = 
      observedValues.dominance_mean !== null 
        ? Math.min(100, Math.max(0, (Math.abs(Number(observedValues.dominance_mean)) / DOMINANCE_MAX) * 100))
        : 0;
    
    const pitchVariationPercent = 
      observedValues.pitch_std !== null 
        ? Math.min(100, Math.max(0, (Number(observedValues.pitch_std) / PITCH_STD_MAX) * 100))
        : 0;
    
    const energiVariationPercent = 
      observedValues.full_observed_values.energy_std !== null 
        ? Math.min(100, Math.max(0, (Number(observedValues.full_observed_values.energy_std) / ENERGY_STD_MAX) * 100))
        : 0;

    // Returner både rådata og beregnede procenter
    return {
      rawData: observedValues,
      kompetenceScores: {
        dominance: {
          værdi: observedValues.dominance_mean,
          procent: dominancePercent,
          label: 'Autoritet'
        },
        pitchVariation: {
          værdi: observedValues.pitch_std,
          procent: pitchVariationPercent,
          label: 'Tonehøjde Variation'
        },
        talehastighed: {
          værdi: observedValues.full_observed_values.energy_std,
          procent: energiVariationPercent,
          label: 'Energy Variation'
        }
      }
    };
  } catch (error) {
    console.error('Fejl ved hentning af kompetencedata:', error);
    return null;
  }
}

/**
 * Henter alle opkaldsdata for en bruger fra Firebase
 * @param authUid Firebase authentication user ID
 * @returns Et array med alle opkaldsdata for brugeren eller null hvis ingen data findes
 */
export async function hentOpkaldsData(authUid: string): Promise<OpkaldsData[] | null> {
  if (!authUid) {
    console.error('Intet auth bruger-ID angivet til hentOpkaldsData');
    return null;
  }

  console.log('hentOpkaldsData: Starter hentning af opkaldsdata for auth ID:', authUid);

  try {
    // 1. Hent brugerens interne ID (user_id) fra users collection
    const internUserId = await hentBrugerInternId(authUid);
    
    if (!internUserId) {
      console.error('hentOpkaldsData: Kunne ikke finde det interne bruger-ID for auth ID:', authUid);
      return null;
    }
    
    console.log(`hentOpkaldsData: Fandt internt user_id: ${internUserId}`);
    
    // 2. Hent alle dokumenter fra rule_result_agent
    const callsCollection = collection(db, 'rule_result_agent');
    
    // Først hent alle dokumenter
    console.log('hentOpkaldsData: Henter alle dokumenter fra rule_result_agent');
    const allCallsSnapshot = await getDocs(callsCollection);
    console.log(`hentOpkaldsData: Fandt total ${allCallsSnapshot.size} dokumenter`);

    // Filtrer dokumenter baseret på user_id
    const matchingCalls = allCallsSnapshot.docs.filter(doc => {
      const data = doc.data();
      const matches = data.user_id === internUserId;
      console.log(`Dokument ${doc.id} har user_id: ${data.user_id}, matcher ${internUserId}: ${matches}`);
      return matches;
    });

    console.log(`hentOpkaldsData: Fandt ${matchingCalls.length} dokumenter der matcher user_id: ${internUserId}`);

    if (matchingCalls.length === 0) {
      console.log(`hentOpkaldsData: Ingen dokumenter fundet med user_id: ${internUserId}`);
      return [];
    }

    // 3. Konverter dokumenter til et array af opkaldsdata
    const opkald: OpkaldsData[] = matchingCalls.map(doc => {
      const data = doc.data();
      console.log(`hentOpkaldsData: Dokument data for ${doc.id}:`, data);
      
      // Konverter talk ratio til decimal hvis det kommer som procent
      let tlr = data.tlr;
      if (typeof tlr === 'number' && tlr > 1) {
        tlr = tlr / 100; // Konverter procent til decimal
      }
      
      return { 
        id: doc.id,
        ...data,
        tlr: tlr
      };
    });

    // Sorter efter timestamp hvis det findes
    opkald.sort((a: OpkaldsData, b: OpkaldsData) => {
      const aTimestamp = a.timestamp;
      const bTimestamp = b.timestamp;
      
      if (aTimestamp && bTimestamp) {
        const timeA = typeof aTimestamp === 'object' && 'seconds' in aTimestamp 
          ? aTimestamp.seconds 
          : typeof aTimestamp === 'number' 
            ? aTimestamp 
            : Number(aTimestamp);
            
        const timeB = typeof bTimestamp === 'object' && 'seconds' in bTimestamp
          ? bTimestamp.seconds
          : typeof bTimestamp === 'number'
            ? bTimestamp
            : Number(bTimestamp);
            
        return timeB - timeA; // Nyeste først
      }
      return 0;
    });

    console.log(`hentOpkaldsData: Returnerer ${opkald.length} sorterede opkaldsdokumenter`);
    return opkald;
  } catch (error) {
    console.error('hentOpkaldsData: Fejl ved hentning af opkaldsdata:', error);
    return null;
  }
} 